const md = `# Azure Speech — Voiceover Setup

The Eyvo Demo Agent ships with three TTS providers:

| Provider     | Status | Where the key lives |
|--------------|--------|---------------------|
| **OpenAI TTS-1-HD** | ✅ Live  | Emergent LLM key (server-side) |
| **ElevenLabs Lily** | ✅ Live  | \`ELEVENLABS_API_KEY\` in backend \`.env\` |
| **Azure Speech**    | ✅ **Live (wired 2026-05-26)** | \`AZURE_SPEECH_KEY\`, \`AZURE_SPEECH_REGION\` in backend \`.env\` |

This page tells an admin everything they need to bring Azure online
once Microsoft has issued the key.

## Why offer Azure?

Two reasons:

1. **Procurement-friendly billing** — many Eyvo customers already
   have Azure consumption commits and prefer adding TTS to that bill
   rather than approving a new vendor.
2. **Voice variety** — \`en-US-JennyMultilingualNeural\` and
   \`en-GB-RyanNeural\` give a different tonal range than ElevenLabs
   Lily, which is helpful for matching customer brand voice.

## What you need from Azure

1. An **Azure Speech resource** (free tier F0 works for evaluation;
   S0 for production).  Create it at
   <https://portal.azure.com> → \`+ Create a resource\` →
   *Cognitive Services* → *Speech*.
2. After provisioning, open the resource → **Keys and Endpoint**.
   Copy:
   - **KEY 1** (a 32-char hex string)
   - **Location/Region** (e.g. \`eastus\`, \`westeurope\`,
     \`southeastasia\`)

The key authenticates every TTS call.  It is **never logged** by the
agent — see the redaction filter in
\`/app/backend/security/auth.py::install_redaction_filter\`, which
matches \`/password|secret|token|key/i\` and replaces the value with
\`***\`.  It is also **never sent to the frontend**; only the backend
calls Azure.

## Wiring the credentials

1. SSH to the host and open the backend env file:
   \`\`\`
   vi /app/backend/.env
   \`\`\`
2. Add two lines (do NOT quote, do NOT add a comment on the same line):
   \`\`\`
   AZURE_SPEECH_KEY=<paste-key-1-here>
   AZURE_SPEECH_REGION=<e.g. eastus>
   \`\`\`
3. Restart the backend so the new env vars are picked up:
   \`\`\`
   sudo supervisorctl restart backend
   \`\`\`
4. Tail the backend log to confirm no startup errors:
   \`\`\`
   tail -n 50 /var/log/supervisor/backend.err.log
   \`\`\`
   You should see the usual startup line followed by no exceptions.

## Switching a workflow to Azure

Voice selection happens per workflow.  Two ways:

**Option A — UI (recommended)**.  Open the workflow detail page,
expand the *Voiceover* panel, and pick **Azure Speech →
\`en-US-JennyMultilingualNeural\`** from the voice dropdown.  Save.
The next time the auto-pipeline runs, it'll use Azure for that
workflow's MP4.

**Option B — \`system_config\` admin route**.  For tenant-wide
defaults, hit:
\`\`\`
PATCH /api/system-config
{ "tts_default_provider": "azure", "tts_default_voice": "en-US-JennyMultilingualNeural" }
\`\`\`
This becomes the new default for any newly-created workflow.

## How to verify it's actually using Azure

1. Trigger a voiceover for a single run (Voiceover panel →
   **Generate voiceover**).
2. Watch the backend log:
   \`\`\`
   tail -f /var/log/supervisor/backend.out.log | grep voiceover
   \`\`\`
   You should see \`provider=azure region=<your-region>\` in the
   structured log line for that run.
3. The MP3 in \`/api/voiceover/{run_id}/audio.mp3\` will sound
   noticeably different from the ElevenLabs Lily voice you're used
   to — Jenny has a flatter, more corporate cadence.

## Security & cost notes

- **No key ever leaves the backend.** The frontend asks the backend
  to "render voiceover", and the backend talks to Azure.  Browser
  DevTools will only ever see the resulting MP3 URL.
- **Redaction is automatic.** Even if a downstream exception
  inadvertently includes the key in an error message, the logging
  filter strips it before write.
- **Pay-per-character.** Azure Neural voices bill at ~$16 per 1M
  characters as of 2026-01.  A 5-minute demo ≈ 4,800 characters →
  about \$0.08 per voiceover.  Budget accordingly.
- **Rotate at will.** \`AZURE_SPEECH_KEY\` is re-read from \`.env\` on
  every voiceover call.  Rotate Key 1 in the Azure portal, update
  \`.env\`, then \`sudo supervisorctl restart backend\` — no downtime
  for any other part of the app.

## Status today

The provider at
\`/app/backend/services/tts_providers/azure_speech.py\` is now **fully
wired**.  Once an admin selects **Azure Neural Speech** in the
voiceover dropdown, the next auto-pipeline run will use Azure's REST
endpoint to render the MP3.  The five voices listed above are
available immediately; any additional Azure voice id can also be passed
via the per-run \`azure_voice\` override.

### What changed on 2026-05-26
- \`AZURE_SPEECH_KEY\` + \`AZURE_SPEECH_REGION\` added to \`backend/.env\`
  (key length 84 chars, region \`eastus\`).
- Provider implementation builds SSML with optional
  \`<prosody rate>\` (Azure supports speed knob, unlike ElevenLabs).
- Output format pinned to \`audio-24khz-48kbitrate-mono-mp3\` so the
  existing \`video_stitcher.py\` pipeline accepts it without
  transcoding.
- Long narration is chunked at 4,500-char sentence boundaries and
  re-concatenated to stay under Azure's per-request limit.
- Redaction filter extended to mask \`AZURE_SPEECH_KEY\`,
  \`SUBSCRIPTION_KEY\`, and \`OCP_APIM_SUBSCRIPTION_KEY\` in case the
  key name ever ends up in a log line.
`;

export default md;
